import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../services/apiService';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authCheckCount, setAuthCheckCount] = useState(0);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Retry nach Login-Redirect (wenn URL parameter vorhanden)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hasAuthParams = urlParams.has('code') || urlParams.has('session_state');
    
    if (hasAuthParams && !isAuthenticated && authCheckCount < 3) {
      // Kurze Verzögerung für Session-Erstellung im Backend
      const timer = setTimeout(() => {
        setAuthCheckCount(prev => prev + 1);
        checkAuthStatus();
      }, 500);
      return () => clearTimeout(timer);
    }
    
    // Cleanup URL params nach erfolgreichem Auth
    if (isAuthenticated && hasAuthParams) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [isAuthenticated, authCheckCount]);

  const checkAuthStatus = async () => {
    try {
      setLoading(true);
      const response = await apiService.checkAuth();
      if (response.authenticated) {
        setUser(response.user);
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const login = () => {
    window.location.href = apiService.getLoginUrl();
  };

  const logout = async () => {
    try {
      await apiService.logout();
      setUser(null);
      setIsAuthenticated(false);
      window.location.href = apiService.getLogoutUrl();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    checkAuthStatus
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
